// Painel Super Master > Aplicativo (pedido 2026-09-08: "cria uma aba nova
// na tela do admin master do sistema" pra colocar o APK com número de
// versão) — mesmo padrão de master.notifications.tsx (upload multipart,
// histórico ao invés de "1 slot por chave" porque aqui cada versão é uma
// linha nova, nunca substitui a anterior).
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Smartphone, Download } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppReleases, useUploadAppRelease, useDeleteAppRelease } from "@/hooks/queries/platform";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { MasterAppStoreVersions } from "@/components/master-app-store-versions";

export const Route = createFileRoute("/master/app")({
  component: () => (
    <MasterShell>
      <AppReleasesPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Aplicativo — Super Master — GPS Drive" }] }),
});

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR");
}

function AppReleasesPage() {
  const { data: releases, isLoading } = useAppReleases();
  const [version, setVersion] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadAppRelease();
  const remove = useDeleteAppRelease();

  const handlePickFile = () => {
    if (!version.trim()) {
      toast.error("Informe a versão antes de escolher o arquivo", {
        description: 'Ex.: "1.0"',
      });
      return;
    }
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await upload.mutateAsync({ version: version.trim(), file });
      setVersion("");
      toast.success(`Versão ${version.trim()} enviada`);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao enviar o arquivo");
    }
  };

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Bug real (relatado 2026-09-09, "ele tá pegando como apk" ao baixar um
  // .aab que tem nome de exibição .aab): o atributo HTML `download` só
  // força o nome do arquivo quando o link é MESMA ORIGEM — aqui
  // `resolveGpsDriveUrl(r.url)` aponta pra api.gpsdrive.com.br, origem
  // diferente do frontend (gpsdrive.com.br), então o navegador ignora
  // silenciosamente `download={r.fileName}` e usa o último segmento da URL
  // (a chave gerada no storage, sempre terminando em .apk pra este
  // namespace — ver ALLOWED_APK_MIME_TYPES) como nome do arquivo salvo.
  // Isso sempre afetou TODOS os itens desta lista, não só o .aab — só
  // nunca foi notado porque instalar um .apk não liga pro nome do
  // arquivo. Fix: baixar como blob (mesma origem da PÁGINA, já que
  // blob: é sempre same-origin) e disparar o download a partir dele —
  // `download` funciona de verdade nesse caso.
  const handleDownload = async (r: { url: string; fileName: string }) => {
    setDownloadingId(r.url);
    try {
      const res = await fetch(resolveGpsDriveUrl(r.url));
      if (!res.ok) throw new Error("Falha ao baixar o arquivo");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = r.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Falha ao baixar o arquivo");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      await remove.mutateAsync(pendingDeleteId);
      toast.success("Versão removida");
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao remover a versão");
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Aplicativo</h1>
        <p className="text-sm text-muted-foreground">
          Envie novas versões do APK Android. Cada envio cria uma versão nova no histórico — nada é
          substituído automaticamente.
        </p>
      </div>

      <MasterAppStoreVersions />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nova versão</CardTitle>
          <CardDescription>Informe o número da versão e escolha o arquivo (.apk, .aab, .ipa ou .zip).</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="Versão (ex.: 1.0)"
            className="max-w-[200px]"
            disabled={upload.isPending}
          />
          <Button type="button" onClick={handlePickFile} disabled={upload.isPending}>
            <Upload className="h-3.5 w-3.5" />
            {upload.isPending ? "Enviando…" : "Enviar arquivo"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".apk,.aab,.ipa,.zip,application/vnd.android.package-archive,application/octet-stream,application/zip"
            className="hidden"
            onChange={handleFileChange}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Histórico de versões</CardTitle>
          <CardDescription>{isLoading ? "Carregando…" : `${releases?.length ?? 0} versão(ões) enviada(s)`}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!isLoading && (releases?.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma versão enviada ainda.</p>
          )}
          {releases?.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
                <Smartphone className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Versão {r.version}</div>
                <p className="truncate text-xs text-muted-foreground">
                  {r.fileName} · {formatFileSize(r.fileSize)} · {formatDate(r.createdAt)}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={downloadingId === r.url}
                onClick={() => handleDownload(r)}
              >
                <Download className="h-3.5 w-3.5" />
                {downloadingId === r.url ? "Baixando…" : "Baixar"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 text-red-600 hover:text-red-600"
                onClick={() => setPendingDeleteId(r.id)}
                disabled={remove.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(open) => !open && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover esta versão?</AlertDialogTitle>
            <AlertDialogDescription>
              O arquivo será apagado e deixa de ficar disponível para download. Não afeta quem já instalou.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
